import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { finalizeGoogleOnboarding } from '../services/authService';
import {
  getDashboardRouteForRole,
  getRoleFromAccessToken,
  getAccessToken,
  saveSessionFromOAuthCallback,
} from '../services/session';

const OAuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();

  const state = useMemo(() => {
    const result = saveSessionFromOAuthCallback();
    const query = new URLSearchParams(window.location.search);
    const oauthState = query.get('state') || '';

    return {
      error: result.error,
      oauthState,
    };
  }, []);

  useEffect(() => {
    if (!state.error) {
      const timeout = window.setTimeout(async () => {
        const accessToken = getAccessToken();
        if (accessToken) {
          try {
            await finalizeGoogleOnboarding({
              accessToken,
              oauthState: state.oauthState,
            });
          } catch (_) {
            // No-op: session is already established; fallback routing still works.
          }
        }
        const role = getRoleFromAccessToken(getAccessToken());
        navigate(getDashboardRouteForRole(role));
      }, 1500);

      return () => window.clearTimeout(timeout);
    }

    return undefined;
  }, [navigate, state.error, state.oauthState]);

  if (state.error) {
    return (
      <div className="min-h-screen bg-appbg px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto flex min-h-[80vh] w-full max-w-[560px] items-center justify-center">
          <div className="w-full rounded-[28px] border border-line bg-card p-8 shadow-floating">
            <h1 className="text-[28px] font-extrabold tracking-[-0.02em] text-ink">Google Sign-In Failed</h1>
            <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-[14px] text-red-700">{state.error}</p>
            <div className="mt-6">
              <Link
                to="/login"
                className="inline-flex h-11 items-center justify-center rounded-full bg-primary-700 px-6 text-[14px] font-bold text-white transition hover:bg-primary-800"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[80vh] w-full max-w-[560px] items-center justify-center">
        <div className="w-full rounded-[28px] border border-line bg-card p-8 shadow-floating">
          <h1 className="text-[28px] font-extrabold tracking-[-0.02em] text-ink">Google Sign-In Successful</h1>
          <p className="mt-3 text-[14px] text-muted">Redirecting you to home...</p>
          <div className="mt-6">
            <Link
              to="/"
              className="inline-flex h-11 items-center justify-center rounded-full bg-primary-700 px-6 text-[14px] font-bold text-white transition hover:bg-primary-800"
            >
              Continue
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OAuthCallbackPage;
